import { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { Tab, Table, Tabs, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';

//components
import withLocalization from '~/hoc/withLocalization';
import Button from '~/components/CustomButton/CustomButton.jsx';
import SwitchWidget from '~/containers/Admin/ControlPanel/SwitchWidget';
import LoadingSpinner from '~/elements/LoadingSpinner';

//stores
import notificationStore from '~/library/stores/notificationStore';
import commonStore from '~/library/stores/commonStore';

//utils
import { NOTIFICATION_CATEGORIES, NOTIFICATION_CATEGORY_LABELS } from '~/utils/constants';
import { formatKey } from '~/utils/commonMethods';

const UserNotificationSettings = ({ t, userId, userName }) => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [activeCategory, setActiveCategory] = useState(NOTIFICATION_CATEGORIES[0]);
    const [search, setSearch] = useState('');
    const [hasUserSettings, setHasUserSettings] = useState(true);

    const { masterNotifications } = notificationStore;

    const buildDefaultSettings = () => {
        const defaults = {};
        Object.values(masterNotifications).forEach((group) => {
            group.forEach((master) => {
                const opts = master.delivery_options || {};
                defaults[master.key] = {
                    enabled: master.is_active,
                    allow_push: !!opts.allow_push,
                    allow_email: !!opts.allow_email,
                    allow_system: !!opts.allow_system,
                    is_obligatory: false,
                };
            });
        });
        return defaults;
    };

    const masterNotificationsLoaded = Object.keys(masterNotifications).length > 0;

    useEffect(() => {
        notificationStore.loadMasterNotifications();
    }, []);

    useEffect(() => {
        if (!masterNotificationsLoaded) return;
        const load = async () => {
            setLoading(true);
            try {
                const res = await notificationStore.loadUserNotificationSettings(userId);
                setHasUserSettings(!!res.settings);
                setSettings({ ...buildDefaultSettings(), ...(res.settings || {}) });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [userId, masterNotificationsLoaded]);

    const handleSettingChange = (key, field, value) => {
        setSettings((prev) => {
            const keySettings = { ...prev[key], [field]: value };
            if (field === 'enabled' && !value) {
                keySettings.allow_push = false;
                keySettings.allow_email = false;
                keySettings.allow_system = false;
                keySettings.is_obligatory = false;
            }

            if (field === 'allow_push' && value) {
                keySettings.allow_system = true;
            }
            if (field === 'allow_system' && !value) {
                keySettings.allow_push = false;
            }
            return { ...prev, [key]: keySettings };
        });
    };

    const handleCategoryChange = (category) => {
        setActiveCategory(category);
        setSearch('');
    };

    const matchesSearch = (item) => {
        if (!search.trim()) return true;
        const term = search.trim().toLowerCase();
        return formatKey(item.key).toLowerCase().includes(term) || (item.description || '').toLowerCase().includes(term);
    };

    const buildMasterByKey = () => {
        const masterByKey = {};
        Object.values(masterNotifications).forEach((group) => {
            group.forEach((master) => { masterByKey[master.key] = master; });
        });
        return masterByKey;
    };

    const isMasterDisabled = (master) => !master || !master.is_active || master.is_batch_notification;

    const getMasterDisabledReason = (master) => {
        if (!master || !master.is_active) return t('This notification is currently inactive.');
        if (master.is_batch_notification) return t('This notification can only be managed by the Super Admin.');
        return null;
    };

    const disableSettingsForInactiveMasters = (settingsToSave, masterByKey) => {
        const sanitized = {};
        Object.entries(settingsToSave).forEach(([key, value]) => {
            const master = masterByKey[key];
            sanitized[key] = (master && !master.is_active)
                ? { ...value, enabled: false, allow_push: false, allow_email: false, allow_system: false, is_obligatory: false }
                : value;
        });
        return sanitized;
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = disableSettingsForInactiveMasters(settings, buildMasterByKey());
            await notificationStore.saveUserNotificationSettings(userId, payload);
            setHasUserSettings(true);
            commonStore.addNotification(t('Saved'), null, 'success');
        } catch (e) {
            commonStore.addNotification(e && e.message ? e.message : t('Something went wrong'), null, 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <LoadingSpinner />;

    return (
        <div>
            {!hasUserSettings && (
                <div className="px-3 pt-3">
                    <small className="text-danger fst-italic">
                        <i className="fa fa-exclamation-circle me-1" />
                        {t('No notification settings have been set for this user yet. The company default template will be used to send notifications.')}
                    </small>
                </div>
            )}
            <Row className="px-3 pt-3 pb-2 border-bottom align-items-center">
                <Col md={4}>
                    {userName ? (
                        <span className="fw-600 font-size-14">{userName}</span>
                    ) : (
                        <span />
                    )}
                </Col>
                <Col md={8}>
                    <div className="d-flex conversation-search">
                        <input
                            type="text"
                            className="conversation-search-input"
                            placeholder={t('Search notifications')}
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                        <i className="conversation-search-icon fa fa-search" />
                    </div>
                </Col>
            </Row>
            <Tabs activeKey={activeCategory} onSelect={handleCategoryChange} id="UserNotifTabs" className="mt-2 px-2">
                {NOTIFICATION_CATEGORIES.map((category) => (
                    <Tab key={category} eventKey={category} title={t(NOTIFICATION_CATEGORY_LABELS[category])}>
                        <div className="overflow-auto" style={{ maxHeight: '55vh' }}>
                            <Table hover responsive className="mb-0">
                                <thead>
                                    <tr className="font-size-12">
                                        <th className="text-start">{t('Notification')}</th>
                                        <th className="text-center">{t('Enabled')}</th>
                                        <th className="text-center">{t('Push')}</th>
                                        <th className="text-center">{t('Email')}</th>
                                        <th className="text-center">{t('System')}</th>
                                        <th className="text-center">
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip id={`obligatory-tooltip-${category}`}>
                                                        {t("If enabled, this notification will be sent regardless of the user's notification preferences.")}
                                                    </Tooltip>
                                                }
                                            >
                                                <span>{t('Obligatory')}</span>
                                            </OverlayTrigger>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(masterNotifications[category] || []).filter(matchesSearch).map((notification) => {
                                        const setting = settings[notification.key] || { enabled: true, allow_push: true, allow_email: true, allow_system: true, is_obligatory: false };
                                        const isInactive = !notification.is_active;
                                        const isBatch = !!notification.is_batch_notification;
                                        const masterDisabled = isMasterDisabled(notification);
                                        const opts = notification.delivery_options || {};
                                        const allowPush = !!opts.allow_push;
                                        const allowEmail = !!opts.allow_email;
                                        const allowSystem = !!opts.allow_system;
                                        const enabledValue = isInactive ? false : (isBatch ? true : setting.enabled);
                                        const pushValue = isInactive ? false : (isBatch ? allowPush : (setting.allow_push && setting.enabled));
                                        const emailValue = isInactive ? false : (isBatch ? allowEmail : (setting.allow_email && setting.enabled));
                                        const systemValue = isInactive ? false : (isBatch ? (allowPush || allowSystem) : (setting.allow_system && setting.enabled));
                                        const obligatoryValue = isInactive ? false : (isBatch ? true : (!!setting.is_obligatory && setting.enabled));
                                        return (
                                            <tr key={notification.id} className={masterDisabled ? 'opacity-50' : ''}>
                                                <td className="text-start">
                                                    {masterDisabled ? (
                                                        <OverlayTrigger
                                                            placement="top"
                                                            overlay={<Tooltip id={`user-notif-disabled-${notification.id}`}>{getMasterDisabledReason(notification)}</Tooltip>}
                                                        >
                                                            <div className="font-size-13">{formatKey(notification.key)}</div>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <div className="font-size-13">{formatKey(notification.key)}</div>
                                                    )}
                                                    {notification.description && (
                                                        <small className="text-muted">{notification.description}</small>
                                                    )}
                                                </td>
                                                <td className="text-center">
                                                    <SwitchWidget
                                                        value={enabledValue}
                                                        onChange={(val) => handleSettingChange(notification.key, 'enabled', val)}
                                                        disabled={masterDisabled}
                                                    />
                                                </td>
                                                <td className="text-center">
                                                    {allowPush ? (
                                                        <SwitchWidget
                                                            value={pushValue}
                                                            onChange={(val) => handleSettingChange(notification.key, 'allow_push', val)}
                                                            disabled={masterDisabled || !setting.enabled}
                                                        />
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-center">
                                                    {allowEmail ? (
                                                        <SwitchWidget
                                                            value={emailValue}
                                                            onChange={(val) => handleSettingChange(notification.key, 'allow_email', val)}
                                                            disabled={masterDisabled || !setting.enabled}
                                                        />
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-center">
                                                    {allowSystem ? (
                                                        <SwitchWidget
                                                            value={systemValue}
                                                            onChange={(val) => handleSettingChange(notification.key, 'allow_system', val)}
                                                            disabled={masterDisabled || !setting.enabled}
                                                        />
                                                    ) : <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-center">
                                                    <SwitchWidget
                                                        value={obligatoryValue}
                                                        onChange={(val) => handleSettingChange(notification.key, 'is_obligatory', val)}
                                                        disabled={masterDisabled || !setting.enabled}
                                                    />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    </Tab>
                ))}
            </Tabs>
            <div className="d-flex justify-content-end p-3 border-top">
                <Button fill dynamic_lg onClick={handleSave} disabled={saving}>
                    {saving ? t('Saving...') : t('Save')}
                </Button>
            </div>
        </div>
    );
};

export default withLocalization(observer(UserNotificationSettings));
